import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { dataGridAmbientBaseSx } from '../../utils/colors';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileUpload as FileUploadIcon,
  LockReset as LockResetIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';
import UserFormModal from '../../components/UserFormModal';
import UserProfileDrawer from '../../components/UserProfileDrawer';

const ROLES = ['student', 'faculty', 'admin', 'cbcfaculty'];
const STATUSES = ['active', 'disabled'];

const roleColor = {
  admin:      'error',
  faculty:    'primary',
  cbcfaculty: 'secondary',
  student:    'default',
};

export default function AdminUsersPage() {
  // ── Data state ──────────────────────────────────────────────────────────
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // ── Filter / search / pagination state ──────────────────────────────────
  const [search, setSearch]         = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBatch, setFilterBatch]   = useState('');
  const [deptOptions, setDeptOptions]   = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // ── Modal / drawer state ─────────────────────────────────────────────────
  const [formOpen, setFormOpen]           = useState(false);
  const [editUser, setEditUser]           = useState(null);
  const [viewUser, setViewUser]           = useState(null);
  const [importOpen, setImportOpen]       = useState(false);
  const [csvText, setCsvText]             = useState('');
  const [importing, setImporting]         = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', user: null });
  const [resetPassUser, setResetPassUser] = useState(null);
  const [newPassword, setNewPassword]     = useState('');
  const [resetting, setResetting]         = useState(false);

  // ── Load filter options once ────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get('/admin/users/filter-options')
      .then(({ data }) => {
        setDeptOptions(data.data.departments);
        setBatchOptions(data.data.batches);
      })
      .catch(() => {});
  }, []);

  // ── Fetch users ──────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page:  paginationModel.page + 1,
        limit: paginationModel.pageSize,
      };
      if (search)       params.search     = search;
      if (filterRole)   params.role       = filterRole;
      if (filterDept)   params.department = filterDept;
      if (filterStatus) params.status     = filterStatus;
      if (filterBatch)  params.batch      = filterBatch;

      const { data } = await axiosInstance.get('/admin/users', { params });
      setRows(data.data.map((u) => ({ ...u, id: u._id })));
      setTotal(data.pagination.total);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, search, filterRole, filterDept, filterStatus, filterBatch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 4000); };

  const handleToggleStatus = async (user) => {
    try {
      const { data } = await axiosInstance.patch(`/admin/users/${user._id}/status`);
      flash(data.message);
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update status');
    }
    setConfirmDialog({ open: false, type: '', user: null });
  };

  const handleDelete = async (user) => {
    try {
      await axiosInstance.delete(`/admin/users/${user._id}`);
      flash('User deleted successfully');
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete user');
    }
    setConfirmDialog({ open: false, type: '', user: null });
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await axiosInstance.post(`/admin/users/${resetPassUser._id}/reset-password`, { newPassword });
      flash('Password reset successfully');
      setResetPassUser(null);
      setNewPassword('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await axiosInstance.post('/admin/users/import', { csv: csvText });
      setImportResult(data);
      flash(data.message);
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns = [
    { field: 'name',             headerName: 'Name',              flex: 1,   minWidth: 150 },
    { field: 'email',            headerName: 'Email',             flex: 1.5, minWidth: 200 },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" color={roleColor[value] || 'default'} />
      ),
    },
    { field: 'department',       headerName: 'Department',        width: 140 },
    { field: 'batch',            headerName: 'Batch',             width: 110 },
    { field: 'leetcodeUsername', headerName: 'LeetCode',          width: 130 },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          color={value === 'active' ? 'success' : 'default'}
          variant={value === 'active' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View profile">
            <IconButton size="small" onClick={() => setViewUser(row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => { setEditUser(row); setFormOpen(true); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.status === 'active' ? 'Disable user' : 'Enable user'}>
            <IconButton
              size="small"
              color={row.status === 'active' ? 'warning' : 'success'}
              onClick={() => setConfirmDialog({ open: true, type: 'status', user: row })}
            >
              {row.status === 'active' ? <PersonOffIcon fontSize="small" /> : <PersonAddIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset password">
            <IconButton size="small" onClick={() => setResetPassUser(row)}>
              <LockResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmDialog({ open: true, type: 'delete', user: row })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>User Management</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setImportOpen(true)}>
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditUser(null); setFormOpen(true); }}
          >
            Create User
          </Button>
        </Stack>
      </Stack>

      {/* ── Alerts ── */}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}
      {error      && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Filters ── */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 240 }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Role</InputLabel>
          <Select value={filterRole} label="Role" onChange={(e) => setFilterRole(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Department</InputLabel>
          <Select value={filterDept} label="Department" onChange={(e) => setFilterDept(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {deptOptions.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Batch</InputLabel>
          <Select value={filterBatch} label="Batch" onChange={(e) => setFilterBatch(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {batchOptions.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <Tooltip title="Refresh">
          <IconButton onClick={fetchUsers}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {/* ── DataGrid ── */}
      <Box sx={{ height: 600 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={total}
          loading={loading}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          sx={dataGridAmbientBaseSx}
        />
      </Box>

      {/* ── Create / Edit Modal ── */}
      <UserFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditUser(null); }}
        initialData={editUser}
        deptOptions={deptOptions}
        batchOptions={batchOptions}
        onSaveSuccess={() => { setFormOpen(false); setEditUser(null); fetchUsers(); flash('User saved successfully'); }}
      />

      {/* ── View Profile Drawer ── */}
      <UserProfileDrawer
        open={!!viewUser}
        user={viewUser}
        onClose={() => setViewUser(null)}
      />

      {/* ── Confirm Dialog (disable / delete) ── */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: '', user: null })}>
        <DialogTitle>
          {confirmDialog.type === 'delete' ? 'Delete user?' : (confirmDialog.user?.status === 'active' ? 'Disable user?' : 'Enable user?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === 'delete'
              ? `Permanently delete "${confirmDialog.user?.name}"? This cannot be undone.`
              : `${confirmDialog.user?.status === 'active' ? 'Disable' : 'Enable'} "${confirmDialog.user?.name}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', user: null })}>Cancel</Button>
          <Button
            color={confirmDialog.type === 'delete' ? 'error' : 'warning'}
            variant="contained"
            onClick={() => confirmDialog.type === 'delete'
              ? handleDelete(confirmDialog.user)
              : handleToggleStatus(confirmDialog.user)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reset Password Dialog ── */}
      <Dialog open={!!resetPassUser} onClose={() => { setResetPassUser(null); setNewPassword(''); }}>
        <DialogTitle>Reset Password — {resetPassUser?.name}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            size="small"
            helperText="Minimum 6 characters"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetPassUser(null); setNewPassword(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={resetting}>
            {resetting ? <CircularProgress size={16} /> : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CSV Import Dialog ── */}
      <Dialog open={importOpen} onClose={() => { setImportOpen(false); setImportResult(null); setCsvText(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Users via CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Expected columns: <code>name,email,role,department,batch,leetcodeUsername,password</code><br />
            Roles: student | faculty | admin | cbcfaculty
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Button variant="outlined" component="label" size="small" startIcon={<FileUploadIcon />}>
              Upload File
              <input type="file" accept=".csv,.txt" hidden onChange={handleCsvFile} />
            </Button>
            <Typography variant="caption" color="text.secondary">or paste CSV below</Typography>
          </Stack>

          <TextField
            multiline
            minRows={6}
            fullWidth
            size="small"
            placeholder="name,email,role,department,batch,leetcodeUsername,password&#10;Jane Doe,jane@college.edu,student,CSE,2025,jane_lc,pass123"
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />

          {importResult && (
            <Alert
              severity={importResult.results.errors.length > 0 ? 'warning' : 'success'}
              sx={{ mt: 2 }}
            >
              {importResult.message}
              {importResult.results.errors.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {importResult.results.errors.map((e, i) => (
                    <li key={i}><Typography variant="caption">Row {e.row}: {e.reason}</Typography></li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportOpen(false); setImportResult(null); setCsvText(''); }}>Close</Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || !csvText.trim()}
            startIcon={importing ? <CircularProgress size={14} /> : null}
          >
            {importing ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
